import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLanguage, pick } from "@/i18n";
import { useAuth } from "@/auth";
import { useSEO } from "@/hooks/useSEO";
import AdminExperienceEditor from "@/components/AdminExperienceEditor";
import AdminGalleryManager from "@/components/AdminGalleryManager";
import AdminVideosManager from "@/components/AdminVideosManager";
import AdminStoriesManager from "@/components/AdminStoriesManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  inviata: "bg-medblue/10 text-medblue",
  attesa: "bg-terracotta/20 text-ink",
  confermata: "bg-sage/25 text-ink",
  completata: "bg-sage/40 text-ink",
  annullata: "bg-red-100 text-red-700",
};
const STATUSES = ["inviata", "attesa", "confermata", "completata", "annullata"];

async function adminFetch(path, options = {}) {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...options });
  if (!r.ok) throw new Error(String(r.status));
  return r.json();
}

export default function Admin() {
  const { lang, t } = useLanguage();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editingExp, setEditingExp] = useState(null);

  useSEO(t("admin_title") + " — Flegrea Experience", t("tagline"));

  useEffect(() => {
    if (!user?.is_admin) return;
    adminFetch("/admin/stats").then(setStats).catch(() => {});
    adminFetch("/admin/requests").then(setRequests).catch(() => {});
    adminFetch("/admin/users").then(setUsers).catch(() => {});
    adminFetch("/admin/contacts").then(setContacts).catch(() => {});
    fetch(`${API}/experiences`).then((r) => r.json()).then(setExperiences).catch(() => {});
  }, [user]);

  if (loading) return <div className="py-40 text-center text-ink/40">{t("loading")}</div>;

  if (!user?.is_admin) {
    return (
      <div className="py-40 text-center" data-testid="admin-denied">
        <p className="font-serif text-3xl text-ink">{t("admin_denied")}</p>
      </div>
    );
  }

  const updateStatus = async (kind, id, status) => {
    await adminFetch(`/admin/requests/${kind}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    toast(t("saved_ok"));
  };

  const updateExperience = async (slug, data) => {
    await adminFetch(`/admin/experiences/${slug}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    setExperiences((es) => es.map((e) => (e.slug === slug ? { ...e, ...data } : e)));
    toast(t("saved_ok"));
  };

  const BLANK_EXP = {
    slug: "", destination: "pozzuoli", categories: ["culture"],
    title: { it: "", en: "" }, short: { it: "", en: "" }, description: { it: "", en: "" },
    duration: { it: "", en: "" }, group_size: { it: "", en: "" },
    price_from: 0, featured: false, image: "/images/pozzuoli-coast.jpeg", gallery: [], chapters: [],
  };

  const retireExperience = async (slug) => {
    if (!window.confirm("Ritirare questo pacchetto? Sparirà dal sito ma resterà nel database.")) return;
    await adminFetch(`/admin/experiences/${slug}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ retired: true }),
    });
    setExperiences((es) => es.filter((e) => e.slug !== slug));
    toast(t("saved_ok"));
  };

  const tabs = [
    ["overview", t("tab_overview")],
    ["requests", t("tab_requests")],
    ["experiences", t("tab_experiences")],
    ["users", t("tab_users")],
    ["stories", t("tab_stories")],
    ["contacts", t("tab_contacts")],
    ["gallery", t("tab_gallery")],
  ];

  return (
    <div className="py-14 lg:py-20" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <h1 className="font-serif text-3xl md:text-4xl text-ink" data-testid="admin-title">{t("admin_title")}</h1>

        <div className="flex gap-2 mt-8 border-b border-stone2/50" data-testid="admin-tabs">
          {tabs.map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`text-[11px] font-medium uppercase tracking-[0.15em] px-5 py-3 border-b-2 -mb-px transition-colors duration-300 ${tab === key ? "border-medblue text-medblue" : "border-transparent text-ink/50 hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10" data-testid="admin-overview">
            {[
              [t("stats_requests"), stats.booking_requests + stats.inquiries],
              [t("stats_users"), stats.users],
              [t("stats_experiences"), stats.experiences],
              [t("stats_favorites"), stats.favorites],
            ].map(([label, value], i) => (
              <div key={i} className="border border-stone2/60 p-6" data-testid={`stat-${i}`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-ink/40">{label}</p>
                <p className="font-serif text-4xl text-ink mt-2">{value}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "requests" && (
          <div className="mt-10 space-y-3" data-testid="admin-requests">
            {requests.length === 0 && <p className="text-ink/50 italic">{t("no_requests")}</p>}
            {requests.map((r) => (
              <div key={r.id} className="border border-stone2/60 p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid={`admin-request-${r.id}`}>
                <span className="text-[10px] uppercase tracking-[0.15em] text-ink/40">{r.kind}</span>
                <span className="font-medium text-ink min-w-[180px]">
                  {r.kind === "booking" ? pick(r.experience_title, lang) : t("custom_request")}
                </span>
                <span className="text-ink/60">{r.name || "—"} · {r.email || "—"}</span>
                {r.kind === "booking" && <span className="text-ink/60">{r.date || "—"} · {r.participants}p · ~€{r.estimate}</span>}
                <select
                  value={r.status}
                  onChange={(e) => updateStatus(r.kind, r.id, e.target.value)}
                  data-testid={`admin-status-${r.id}`}
                  className={`ml-auto text-[10px] font-semibold uppercase tracking-[0.15em] px-3 py-2 border-0 focus:outline-none ${STATUS_STYLE[r.status] || STATUS_STYLE.inviata}`}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{t(`status_${s}`)}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}

        {tab === "experiences" && (
          <div className="mt-10 space-y-3" data-testid="admin-experiences">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setEditingExp(BLANK_EXP)}
                data-testid="admin-create-experience"
                className="bg-terracotta text-ink text-[10px] font-medium uppercase tracking-[0.15em] px-5 py-3 hover:bg-ink hover:text-ivory transition-colors duration-300"
              >
                + Crea pacchetto
              </button>
            </div>
            {experiences.map((e) => (
              <div key={e.slug} className="border border-stone2/60 p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid={`admin-exp-${e.slug}`}>
                <span className="font-medium text-ink min-w-[220px]">{pick(e.title, lang)}</span>
                <label className="flex items-center gap-2 text-ink/60">
                  {t("price_edit")} €
                  <input
                    type="number"
                    defaultValue={e.price_from}
                    data-testid={`admin-price-${e.slug}`}
                    onBlur={(ev) => Number(ev.target.value) !== e.price_from && updateExperience(e.slug, { price_from: Number(ev.target.value) })}
                    className="w-20 border border-stone2 px-2 py-1.5 text-sm focus:outline-none focus:border-medblue"
                  />
                </label>
                <label className="flex items-center gap-2 text-ink/60 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!e.featured}
                    onChange={(ev) => updateExperience(e.slug, { featured: ev.target.checked })}
                    data-testid={`admin-featured-${e.slug}`}
                    className="w-4 h-4 accent-[#1D4E89]"
                  />
                  {t("featured_label")}
                </label>
                <button
                  onClick={() => setEditingExp(e)}
                  data-testid={`admin-edit-${e.slug}`}
                  className="ml-auto bg-ink text-ivory text-[10px] font-medium uppercase tracking-[0.15em] px-4 py-2 hover:bg-medblue transition-colors duration-300"
                >
                  {t("admin_edit_content")}
                </button>
                <button
                  onClick={() => retireExperience(e.slug)}
                  data-testid={`admin-retire-${e.slug}`}
                  className="text-red-600/60 hover:text-red-600 text-[10px] font-medium uppercase tracking-[0.15em] px-3 py-2 transition-colors duration-300"
                >
                  Ritira
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "users" && (
          <div className="mt-10 space-y-3" data-testid="admin-users">
            {users.map((u) => (
              <div key={u.user_id} className="border border-stone2/60 p-4 flex items-center gap-4 text-sm" data-testid={`admin-user-${u.user_id}`}>
                <span className="w-8 h-8 rounded-full bg-medblue text-ivory flex items-center justify-center text-xs font-semibold shrink-0">
                  {(u.name || u.email || "?")[0].toUpperCase()}
                </span>
                <span className="font-medium text-ink">{u.name}</span>
                <span className="text-ink/50">{u.email}</span>
              </div>
            ))}
          </div>
        )}
        {tab === "gallery" && (<><AdminGalleryManager /><AdminVideosManager /></>)}

        {tab === "stories" && <AdminStoriesManager />}

        {editingExp && (
          <AdminExperienceEditor
            exp={editingExp}
            onClose={() => setEditingExp(null)}
            onSaved={(updated) => setExperiences((es) => (es.some((e) => e.slug === updated.slug) ? es.map((e) => (e.slug === updated.slug ? updated : e)) : [...es, updated]))}
          />
        )}

        {tab === "contacts" && (
          <div className="mt-10 space-y-3" data-testid="admin-contacts">
            {contacts.length === 0 && <p className="text-ink/50 italic">—</p>}
            {contacts.map((c) => (
              <div key={c.id} className="border border-stone2/60 p-5" data-testid={`admin-contact-${c.id}`}>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                  <span className="font-medium text-ink text-sm">{c.name}</span>
                  <a href={`mailto:${c.email}`} className="text-sm text-medblue">{c.email}</a>
                  <span className="ml-auto text-xs text-ink/40">{new Date(c.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}</span>
                </div>
                <p className="mt-2 text-sm text-ink/60 leading-relaxed">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
